// src/components/RecurringExpensesList.js
import React, { useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccessTime as AccessTimeIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format, isValid } from 'date-fns';
import { it } from 'date-fns/locale';

const RECURRENCE_LABELS = {
  weekly: 'Settimanale',
  biweekly: 'Bisettimanale',
  monthly: 'Mensile',
  bimonthly: 'Bimestrale',
  quarterly: 'Trimestrale',
  yearly: 'Annuale',
};

const formatFirestoreDate = (firestoreDate) => {
  try {
    if (!firestoreDate) return 'Data non disponibile';

    const date = firestoreDate?.toDate ? firestoreDate.toDate() : new Date(firestoreDate);

    if (date instanceof Date && isValid(date)) {
      return format(date, 'dd MMMM yyyy', { locale: it });
    }
    return 'Data non disponibile';
  } catch (error) {
    console.error('Errore nella formattazione della data:', error);
    return 'Data non disponibile';
  }
};

const RecurringExpensesList = ({
  recurringExpenses,
  onUpdate,
  onDelete,
  userFamily,
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    amount: '',
    category: '',
    recurrenceType: '',
  });

  const handleEdit = useCallback((expense) => {
    setSelectedExpense(expense);
    setEditForm({
      name: expense.name || '',
      amount: expense.amount?.toString() || '',
      category: expense.category || '',
      recurrenceType: expense.recurrence?.type || 'monthly',
    });
    setEditDialogOpen(true);
  }, []);

  const handleUpdate = async () => {
    try {
      if (!editForm.name || !editForm.amount) {
        setError('Nome e importo sono campi obbligatori');
        return;
      }

      const updatedExpense = {
        name: editForm.name.trim(),
        amount: parseFloat(editForm.amount),
        category: editForm.category.trim(),
        recurrence: {
          ...selectedExpense.recurrence,
          type: editForm.recurrenceType,
          lastUpdated: new Date(),
        },
      };

      await onUpdate(selectedExpense.id, updatedExpense);
      setSuccessMessage('Spesa ricorrente aggiornata con successo');
      setEditDialogOpen(false);
      setError('');
    } catch (error) {
      console.error("Errore nell'aggiornamento:", error);
      setError("Errore durante l'aggiornamento della spesa");
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      if (window.confirm('Sei sicuro di voler eliminare questa spesa ricorrente?')) {
        await onDelete(expenseId);
        setSuccessMessage('Spesa ricorrente eliminata con successo');
      }
    } catch (error) {
      console.error('Errore nella cancellazione:', error);
      setError("Errore durante l'eliminazione della spesa");
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError('');
  };

  if (!recurringExpenses?.length) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Spese Ricorrenti {userFamily ? 'Familiari' : 'Personali'}
        </Typography>
        <Typography color="textSecondary">
          Non ci sono spese ricorrenti {userFamily ? 'familiari' : 'personali'}.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Spese Ricorrenti {userFamily ? 'Familiari' : 'Personali'}
        </Typography>

        <List>
          {recurringExpenses.map((expense) => (
            <Accordion key={expense.id} defaultExpanded={false} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">{expense.name}</Typography>
                    <Chip
                      size="small"
                      label={`€${expense.amount?.toFixed(2)}`}
                      color="primary"
                    />
                    <Chip
                      size="small"
                      label={RECURRENCE_LABELS[expense.recurrence?.type] || 'Non specificata'}
                      icon={<AccessTimeIcon />}
                      color="secondary"
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Categoria:</strong> {expense.category || 'Non specificata'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Creata il:</strong> {formatFirestoreDate(expense.createdAt)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Ultimo addebito:</strong> {formatFirestoreDate(expense.recurrence?.lastProcessed)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Prossimo addebito:</strong> {formatFirestoreDate(expense.recurrence?.nextProcessingDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Tooltip title="Modifica">
                    <IconButton
                      onClick={() => handleEdit(expense)}
                      sx={{ ml: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Elimina">
                    <IconButton
                      onClick={() => handleDelete(expense.id)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </List>
      </Paper>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Modifica Spesa Ricorrente</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nome"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Importo (€)"
            type="number"
            value={editForm.amount}
            onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Categoria"
            value={editForm.category}
            onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label="Frequenza"
            value={editForm.recurrenceType}
            onChange={(e) => setEditForm({ ...editForm, recurrenceType: e.target.value })}
            margin="normal"
          >
            {Object.entries(RECURRENCE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleUpdate} color="primary" variant="contained">
            Salva Modifiche
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage || !!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={successMessage ? "success" : "error"}
          sx={{ width: '100%' }}
        >
          {successMessage || error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RecurringExpensesList;
